import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, SaveIcon, EyeIcon, Loader2Icon, HistoryIcon } from 'lucide-react';
import { CodeEditor } from '../components/CodeEditor';
import { PreviewModal } from '../components/PreviewModal';
import { FormPreview } from '../components/FormPreview';
import { GptCodeAssistant } from '../components/GptCodeAssistant';
import { FormBuilder } from '../components/builder/FormBuilder';
import { VersionHistoryPanel } from '../components/VersionHistoryPanel';
import { auditService } from '../services/auditService';
import { FormBlock, FormTheme, FormBuilderSchema, DEFAULT_FORM_THEME } from '../components/builder/types';
import { formTemplatesLibraryApi, FormTemplateRecord, formVersionsApi, FormVersionRecord } from '../services/creatorApi';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { toast } from 'sonner';
import { ConfirmModal } from '../components/ConfirmModal';
const CATEGORIES = ['Newsletter', 'Contact', 'Event', 'Registration', 'Feedback', 'Other'];
// Welcome screen config interface (matches SignupFormEditor)
interface WelcomeScreenConfig {
  enabled: boolean;
  title: string;
  description: string;
  buttonText: string;
  imageUrl: string;
  estimatedTime: string;
  backgroundColor: string;
  textColor: string;
}
export function FormTemplateLibraryEditor() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  const {
    currentWorkspace,
    currentSubRegion,
    currentUser
  } = useWorkspace();
  const isEditing = !!id && id !== 'new';
  // FIX: Team is always the workspace name, Region is the sub-region
  const team = currentWorkspace.name.replace(/\s*Team\s*$/i, '');
  const region = currentSubRegion || '';
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [showBuilderConfirm, setShowBuilderConfirm] = useState(false);
  const [editorMode, setEditorMode] = useState<'code' | 'builder'>('code');
  const [activeTab, setActiveTab] = useState<'html' | 'css' | 'js'>('html');
  const [templateName, setTemplateName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [formData, setFormData] = useState({
    html: '',
    css: '',
    js: ''
  });
  const [builderBlocks, setBuilderBlocks] = useState<FormBlock[]>([]);
  const [builderTheme, setBuilderTheme] = useState<FormTheme>(DEFAULT_FORM_THEME);
  // Welcome screen state
  const [welcomeScreenEnabled, setWelcomeScreenEnabled] = useState(false);
  const [welcomeTitle, setWelcomeTitle] = useState('');
  const [welcomeDescription, setWelcomeDescription] = useState('');
  const [welcomeButtonText, setWelcomeButtonText] = useState('Join Us');
  const [welcomeImageUrl, setWelcomeImageUrl] = useState('');
  const [welcomeEstimatedTime, setWelcomeEstimatedTime] = useState('');
  const [welcomeBgColor, setWelcomeBgColor] = useState('#000000');
  const [welcomeTextColor, setWelcomeTextColor] = useState('#ffffff');
  useEffect(() => {
    if (isEditing && id) {
      setIsLoading(true);
      formTemplatesLibraryApi.get(id).then(record => {
        if (record) {
          setTemplateName(record.Template_Name || '');
          setDescription(record.Description || '');
          setCategory(record.Category || '');
          setFormData({
            html: record.HTML || '',
            css: record.CSS || '',
            js: record.JavaScript1 || ''
          });
          if (record.Builder_Mode === 'builder' && record.Builder_Schema) {
            try {
              const schema: FormBuilderSchema & {
                welcomeScreen?: WelcomeScreenConfig;
              } = JSON.parse(record.Builder_Schema);
              setBuilderBlocks(schema.blocks || []);
              setBuilderTheme({
                ...DEFAULT_FORM_THEME,
                ...(schema.theme || {})
              });
              setEditorMode('builder');
              // Restore welcome screen config
              if (schema.welcomeScreen) {
                setWelcomeScreenEnabled(schema.welcomeScreen.enabled || false);
                setWelcomeTitle(schema.welcomeScreen.title || '');
                setWelcomeDescription(schema.welcomeScreen.description || '');
                setWelcomeButtonText(schema.welcomeScreen.buttonText || 'Join Us');
                setWelcomeImageUrl(schema.welcomeScreen.imageUrl || '');
                setWelcomeEstimatedTime(schema.welcomeScreen.estimatedTime || '');
                setWelcomeBgColor(schema.welcomeScreen.backgroundColor || '#000000');
                setWelcomeTextColor(schema.welcomeScreen.textColor || '#ffffff');
              }
            } catch {
              /* fallback to code */
            }
          } else if (record.Builder_Schema) {
            try {
              const schema: FormBuilderSchema & {
                welcomeScreen?: WelcomeScreenConfig;
              } = JSON.parse(record.Builder_Schema);
              setBuilderBlocks(schema.blocks || []);
              setBuilderTheme({
                ...DEFAULT_FORM_THEME,
                ...(schema.theme || {})
              });
              // Restore welcome screen config
              if (schema.welcomeScreen) {
                setWelcomeScreenEnabled(schema.welcomeScreen.enabled || false);
                setWelcomeTitle(schema.welcomeScreen.title || '');
                setWelcomeDescription(schema.welcomeScreen.description || '');
                setWelcomeButtonText(schema.welcomeScreen.buttonText || 'Join Us');
                setWelcomeImageUrl(schema.welcomeScreen.imageUrl || '');
                setWelcomeEstimatedTime(schema.welcomeScreen.estimatedTime || '');
                setWelcomeBgColor(schema.welcomeScreen.backgroundColor || '#000000');
                setWelcomeTextColor(schema.welcomeScreen.textColor || '#ffffff');
              }
            } catch {
              /* ignore */
            }
          }
        } else {
          toast.error('Template not found');
          navigate('/form-templates');
        }
      }).catch(err => {
        toast.error('Failed to load template', {
          description: err instanceof Error ? err.message : 'Unknown error'
        });
      }).finally(() => setIsLoading(false));
    }
  }, [id, isEditing, navigate]);
  const handleSave = async () => {
    if (!templateName.trim()) {
      toast.error('Template Name is required');
      return;
    }
    setIsSaving(true);
    // Build welcome screen config
    const welcomeConfig: WelcomeScreenConfig = {
      enabled: welcomeScreenEnabled,
      title: welcomeTitle,
      description: welcomeDescription,
      buttonText: welcomeButtonText,
      imageUrl: welcomeImageUrl,
      estimatedTime: welcomeEstimatedTime,
      backgroundColor: welcomeBgColor,
      textColor: welcomeTextColor
    };
    const payload: Record<string, any> = {
      Template_Name: templateName,
      Description: description,
      Category: category,
      HTML: formData.html,
      CSS: formData.css,
      JavaScript1: formData.js,
      Builder_Mode: editorMode,
      Team: team,
      Region: region
    };
    // Always save Builder_Schema to persist welcome screen config
    const schemaData: Record<string, any> = {
      welcomeScreen: welcomeConfig
    };
    if (builderBlocks.length > 0 || editorMode === 'builder') {
      schemaData.blocks = builderBlocks;
      schemaData.theme = builderTheme;
    }
    payload.Builder_Schema = JSON.stringify(schemaData);
    try {
      let savedTemplateId = id || '';
      if (isEditing && id) {
        await formTemplatesLibraryApi.update(id, payload);
        savedTemplateId = id;
        toast.success('Form template updated');
        (async () => {
          try {
            const versions = await formVersionsApi.listByFormId(id);
            // Deactivate all existing versions
            for (const v of versions) {
              if (v.Is_Active === true) {
                try {
                  await formVersionsApi.update(v.ID, {
                    Is_Active: false
                  });
                } catch (e) {
                  console.warn('Failed to deactivate version:', e);
                }
              }
            }
            const nextVersion = versions.length > 0 ? Math.max(...versions.map(v => v.Version_Number || 0)) + 1 : 1;
            await formVersionsApi.create({
              Form_ID: id,
              Form_Name: templateName,
              Version_Number: nextVersion,
              HTML: formData.html,
              CSS: formData.css,
              JavaScript1: formData.js,
              Builder_Schema: payload.Builder_Schema || '',
              Created_By_Name: currentUser.User_Name,
              Created_By_Email: currentUser.Email,
              Change_Summary: 'Updated template',
              Team: team,
              Region: region,
              Is_Active: true
            });
          } catch (err) {
            console.error('Failed to create form version:', err);
          }
          auditService.log({
            actionType: 'updated',
            entityType: 'form_template',
            entityId: id,
            entityName: templateName,
            userName: currentUser.User_Name,
            userEmail: currentUser.Email,
            userRole: currentUser.Role,
            team: currentWorkspace.name,
            region: currentSubRegion || ''
          });
        })();
      } else {
        const result = await formTemplatesLibraryApi.create(payload);
        if (result?.data?.ID) {
          savedTemplateId = String(result.data.ID);
        }
        toast.success('Form template created');
        (async () => {
          try {
            await formVersionsApi.create({
              Form_ID: savedTemplateId,
              Form_Name: templateName,
              Version_Number: 1,
              HTML: formData.html,
              CSS: formData.css,
              JavaScript1: formData.js,
              Builder_Schema: payload.Builder_Schema || '',
              Created_By_Name: currentUser.User_Name,
              Created_By_Email: currentUser.Email,
              Change_Summary: 'Initial version',
              Team: team,
              Region: region,
              Is_Active: true
            });
          } catch (err) {
            console.error('Failed to create form version:', err);
          }
          auditService.log({
            actionType: 'created',
            entityType: 'form_template',
            entityId: savedTemplateId,
            entityName: templateName,
            userName: currentUser.User_Name,
            userEmail: currentUser.Email,
            userRole: currentUser.Role,
            team: currentWorkspace.name,
            region: currentSubRegion || ''
          });
        })();
        navigate('/form-templates');
      }
    } catch (err) {
      toast.error('Failed to save template', {
        description: err instanceof Error ? err.message : 'Unknown error'
      });
    } finally {
      setIsSaving(false);
    }
  };
  const handleRestoreVersion = async (version: FormVersionRecord | any) => {
    const formVersion = version as FormVersionRecord;
    setFormData({
      html: formVersion.HTML || '',
      css: formVersion.CSS || '',
      js: formVersion.JavaScript1 || ''
    });
    if (formVersion.Builder_Schema) {
      try {
        const schema = JSON.parse(formVersion.Builder_Schema);
        if (schema.blocks && schema.blocks.length > 0) {
          setBuilderBlocks(schema.blocks);
          setBuilderTheme({
            ...DEFAULT_FORM_THEME,
            ...(schema.theme || {})
          });
          setEditorMode('builder');
        } else {
          setEditorMode('code');
        }
        if (schema.welcomeScreen) {
          setWelcomeScreenEnabled(schema.welcomeScreen.enabled || false);
          setWelcomeTitle(schema.welcomeScreen.title || '');
          setWelcomeDescription(schema.welcomeScreen.description || '');
          setWelcomeButtonText(schema.welcomeScreen.buttonText || 'Join Us');
          setWelcomeImageUrl(schema.welcomeScreen.imageUrl || '');
          setWelcomeEstimatedTime(schema.welcomeScreen.estimatedTime || '');
          setWelcomeBgColor(schema.welcomeScreen.backgroundColor || '#000000');
          setWelcomeTextColor(schema.welcomeScreen.textColor || '#ffffff');
        }
      } catch {
        setEditorMode('code');
      }
    } else {
      setEditorMode('code');
    }
    // Save the restored content to the main record
    if (id && isEditing) {
      try {
        await formTemplatesLibraryApi.update(id, {
          HTML: formVersion.HTML || '',
          CSS: formVersion.CSS || '',
          JavaScript1: formVersion.JavaScript1 || '',
          Builder_Schema: formVersion.Builder_Schema || '',
          Builder_Mode: formVersion.Builder_Schema && JSON.parse(formVersion.Builder_Schema).blocks?.length > 0 ? 'builder' : 'code'
        });
        auditService.log({
          actionType: 'restored',
          entityType: 'form_template',
          entityId: id,
          entityName: templateName,
          summary: `Restored to Version ${formVersion.Version_Number}`,
          userName: currentUser.User_Name,
          userEmail: currentUser.Email,
          userRole: currentUser.Role,
          team: currentWorkspace.name,
          region: currentSubRegion || ''
        });
      } catch (err) {
        console.error('Failed to update record after restore:', err);
        throw err;
      }
    }
  };
  if (isLoading) {
    return <div className="flex items-center justify-center py-24" data-id="element-4455" data-mp="2x68ttzeuybgs">
        <Loader2Icon className="h-8 w-8 text-blue-500 animate-spin" data-id="element-4456" data-mp="3kujo2jq1tcw3" />
        <span className="ml-3 text-sm text-gray-500" data-id="element-4457" data-mp="3kxe8wccz4zq1">Loading template...</span>
      </div>;
  }
  return <div className="space-y-6" data-id="element-4458" data-mp="3c2q4eof83m2a">
      <div className="flex items-center justify-between" data-id="element-4459" data-mp="3c3jdhw1nmnnl">
        <div className="flex items-center gap-4" data-id="element-4460" data-mp="3c0x55u6xw44b">
          <Link to="/form-templates" className="p-2 hover:bg-gray-100 rounded-full text-gray-500" data-id="element-4461" data-mp="3g3a33wfg2xqj">
            <ArrowLeftIcon className="h-5 w-5" data-id="element-4462" data-mp="7mf8t5cpqlo2" />
          </Link>
          <div data-id="element-4463" data-mp="cg1mn0um4vlg">
            <h1 className="text-2xl font-semibold text-gray-900" data-id="element-4464" data-mp="cgta1oe23n0n">
              {isEditing ? 'Edit Form Template' : 'Create Form Template'}
            </h1>
            <p className="text-sm text-gray-500" data-id="element-4465" data-mp="1s73vzwav8y9g">
              {editorMode === 'builder' ? 'Drag and drop blocks to build your template.' : 'Write HTML, CSS, and JS for this template.'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3" data-id="element-4466" data-mp="21yq2jfc9e95v">
          <div className="inline-flex rounded-md border border-gray-300 bg-white p-0.5" data-id="element-4467" data-mp="1e7ufswmnu45h">
            <button onClick={() => setEditorMode('code')} className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${editorMode === 'code' ? 'bg-gray-900 text-white' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-4468" data-mp="198htqe3m513y">
              Code
            </button>
            <button onClick={() => {
            if (editorMode === 'code' && formData.html && builderBlocks.length === 0) {
              setShowBuilderConfirm(true);
              return;
            }
            setEditorMode('builder');
          }} className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${editorMode === 'builder' ? 'bg-gray-900 text-white' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-4469" data-mp="2zjcn3tjvk0ym">
              Builder
            </button>
          </div>
          {isEditing && <button onClick={() => setShowVersionHistory(true)} className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50" data-id="element-4470" data-mp="2izkq7nynhfan">
              <HistoryIcon className="h-4 w-4" data-id="element-4471" data-mp="2xmw3buahd7cv" /> History
            </button>}
          <button onClick={() => setShowPreview(true)} className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50" data-id="element-4472" data-mp="207rz5z35645k">
            <EyeIcon className="h-4 w-4" data-id="element-4473" data-mp="map0iq5t45p5" /> Preview
          </button>
          <button onClick={handleSave} disabled={isSaving} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50" data-id="element-4474" data-mp="i9dib5p9imqz">
            {isSaving ? <Loader2Icon className="h-4 w-4 animate-spin" data-id="element-4475" data-mp="3c4db971dkgs" /> : <SaveIcon className="h-4 w-4" data-id="element-4476" data-mp="3e7dun5tevzr" />}
            {isSaving ? 'Saving...' : isEditing ? 'Update' : 'Create'}
          </button>
        </div>
      </div>

      {/* Welcome Screen — Full-width configuration section */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden" data-id="element-4477" data-mp="3lr55m6sgeiiu">
        <div className="px-5 py-3.5 flex items-center justify-between border-b border-gray-100 bg-gray-50/60" data-id="element-4478" data-mp="3grsmnx4pdvlj">
          <div className="flex items-center gap-3" data-id="element-4479" data-mp="3bt7ijmmbyrrt">
            <div className="h-7 w-7 rounded-md bg-indigo-50 flex items-center justify-center" data-id="element-4480" data-mp="3buimpnjou1jg">
              <svg className="h-4 w-4 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} data-id="element-4481" data-mp="14f4w6qud7xr5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" data-id="element-4482" data-mp="zhmlow0p9ah1" />
              </svg>
            </div>
            <div data-id="element-4483" data-mp="bstu1br41os8">
              <h3 className="text-sm font-semibold text-gray-900" data-id="element-4484" data-mp="btd280qykyt2">
                Welcome Screen
              </h3>
              <p className="text-xs text-gray-500" data-id="element-4485" data-mp="grwoy3ddmemh">
                Optional intro screen shown before the form
              </p>
            </div>
          </div>
          <button type="button" onClick={() => setWelcomeScreenEnabled(!welcomeScreenEnabled)} className={`${welcomeScreenEnabled ? 'bg-blue-600' : 'bg-gray-200'} relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none`} data-id="element-4486" data-mp="6tqolq77xgjq">
            <span className={`${welcomeScreenEnabled ? 'translate-x-4' : 'translate-x-0'} pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`} data-id="element-4487" data-mp="3obw9re93l3tv" />
          </button>
        </div>

        {welcomeScreenEnabled && <div className="p-5" data-id="element-4488" data-mp="fvtde4nfr1ku">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4" data-id="element-4489" data-mp="furpv0p8gmm8">
              <div className="space-y-1.5" data-id="element-4490" data-mp="awq7khzmsrbt">
                <label className="block text-sm font-medium text-gray-700" data-id="element-4491" data-mp="axrtjhi6qy8b">
                  Title
                </label>
                <input type="text" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={welcomeTitle} onChange={e => setWelcomeTitle(e.target.value)} placeholder="Tell Everyone!" data-id="element-4492" data-mp="kv608peb48nf" />
              </div>
              <div className="space-y-1.5" data-id="element-4493" data-mp="16dstd88hn06d">
                <label className="block text-sm font-medium text-gray-700" data-id="element-4494" data-mp="12ch4x61cokzr">
                  Button Text
                </label>
                <input type="text" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={welcomeButtonText} onChange={e => setWelcomeButtonText(e.target.value)} placeholder="Join Us" data-id="element-4495" data-mp="eo7n0f79chdw" />
              </div>
              <div className="sm:col-span-2 space-y-1.5" data-id="element-4496" data-mp="3r41g0954ntzd">
                <label className="block text-sm font-medium text-gray-700" data-id="element-4497" data-mp="3r2gmmmujuh33">
                  Description
                </label>
                <textarea rows={3} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={welcomeDescription} onChange={e => setWelcomeDescription(e.target.value)} placeholder="The movement is growing every day..." data-id="element-4498" data-mp="3cyrgwzp5hm90" />
              </div>
              <div className="space-y-1.5" data-id="element-4499" data-mp="3vqtyqedj38ku">
                <label className="block text-sm font-medium text-gray-700" data-id="element-4500" data-mp="3vnpkeo6cmwgg">
                  Hero Image URL{' '}
                  <span className="text-gray-400 font-normal" data-id="element-4501" data-mp="3lrmmxvmfzlzu">(Optional)</span>
                </label>
                <input type="url" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={welcomeImageUrl} onChange={e => setWelcomeImageUrl(e.target.value)} placeholder="https://..." data-id="element-4502" data-mp="3qqpecd9i1tpc" />
              </div>
              <div className="space-y-1.5" data-id="element-4503" data-mp="luwe8p0gkn2u">
                <label className="block text-sm font-medium text-gray-700" data-id="element-4504" data-mp="gtgtyz63ukli">
                  Estimated Time{' '}
                  <span className="text-gray-400 font-normal" data-id="element-4505" data-mp="c1yuygnn6u36">(Optional)</span>
                </label>
                <input type="text" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={welcomeEstimatedTime} onChange={e => setWelcomeEstimatedTime(e.target.value)} placeholder="2 minutes" data-id="element-4506" data-mp="734cjftl77jl" />
              </div>
              <div className="space-y-1.5" data-id="element-4507" data-mp="3kb8pgnjg9mog">
                <label className="block text-sm font-medium text-gray-700" data-id="element-4508" data-mp="3kcjyb1rr5kmf">
                  Background Color
                </label>
                <div className="flex items-center gap-2" data-id="element-4509" data-mp="1iikmmfapx197">
                  <input type="color" value={welcomeBgColor} onChange={e => setWelcomeBgColor(e.target.value)} className="h-8 w-8 rounded border border-gray-300 cursor-pointer flex-shrink-0" data-id="element-4510" data-mp="1igzrookhrgat" />
                  <input type="text" value={welcomeBgColor} onChange={e => setWelcomeBgColor(e.target.value)} className="block w-32 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm font-mono uppercase" data-id="element-4511" data-mp="1zt6mavpdji26" />
                  <div className="h-6 w-12 rounded" style={{
                backgroundColor: welcomeBgColor
              }} data-id="element-4512" data-mp="1h1dv96tv86x3" />
                </div>
              </div>
              <div className="space-y-1.5" data-id="element-4513" data-mp="2sgkded6e5jed">
                <label className="block text-sm font-medium text-gray-700" data-id="element-4514" data-mp="2xg5b6k9trlpi">
                  Text Color
                </label>
                <div className="flex items-center gap-2" data-id="element-4515" data-mp="2v0qiwpanlhx1">
                  <input type="color" value={welcomeTextColor} onChange={e => setWelcomeTextColor(e.target.value)} className="h-8 w-8 rounded border border-gray-300 cursor-pointer flex-shrink-0" data-id="element-4516" data-mp="2v3bjlaz7trt7" />
                  <input type="text" value={welcomeTextColor} onChange={e => setWelcomeTextColor(e.target.value)} className="block w-32 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm font-mono uppercase" data-id="element-4517" data-mp="302xwtkf1n820" />
                  <div className="h-6 w-12 rounded border border-gray-200" style={{
                backgroundColor: welcomeTextColor
              }} data-id="element-4518" data-mp="3is4auu4w7bhh" />
                </div>
              </div>
            </div>
          </div>}
      </div>

      {editorMode === 'builder' ? <div className="space-y-6" data-id="element-4519" data-mp="k79mmk16a5jm">
          <FormBuilder initialBlocks={builderBlocks} initialTheme={builderTheme} onSchemaChange={(blocks, theme) => {
        setBuilderBlocks(blocks);
        setBuilderTheme(theme);
      }} onHtmlChange={(html, css) => {
        setFormData(prev => ({
          ...prev,
          html,
          css
        }));
      }} data-id="element-4520" data-mp="k7jbu5ee88o9" />
          <div className="grid grid-cols-3 gap-6" data-id="element-4521" data-mp="1ai7mmsnycld">
            <div className="col-span-2" data-id="element-4522" data-mp="3jn69sjy65tza" />
            <SettingsPanel templateName={templateName} setTemplateName={setTemplateName} description={description} setDescription={setDescription} category={category} setCategory={setCategory} team={team} region={region} data-id="element-4523" data-mp="3jo7wxmsgm6xd" />
          </div>
        </div> : <div className="grid grid-cols-3 gap-6" data-id="element-4524" data-mp="2vk89q2tztiib">
          <div className="col-span-2 space-y-6" data-id="element-4525" data-mp="2qs8f8pwozht4">
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm" data-id="element-4526" data-mp="2qqdx1jct23sq">
              <div className="border-b border-gray-200 px-4 py-3 flex items-center gap-4" data-id="element-4527" data-mp="2ltng6tt6f469">
                {(['html', 'css', 'js'] as const).map(tab => <button key={tab} onClick={() => setActiveTab(tab)} className={`text-sm font-medium px-3 py-1.5 rounded-md transition-colors ${activeTab === tab ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50'}`} data-id="element-4528" data-mp="3eh9ilyqo42rj">
                    {tab === 'js' ? 'JavaScript' : tab.toUpperCase()}
                  </button>)}
              </div>
              <div className="p-4" data-id="element-4529" data-mp="1t4zoafdx9gjq">
                {activeTab === 'html' && <CodeEditor label="Form HTML" language="html" placeholder="<form>...</form>" value={formData.html} onChange={e => setFormData({
              ...formData,
              html: e.target.value
            })} data-id="element-4530" data-mp="1o4u1pdmiek8i" />}
                {activeTab === 'css' && <CodeEditor label="Custom CSS" language="css" placeholder=".form { ... }" value={formData.css} onChange={e => setFormData({
              ...formData,
              css: e.target.value
            })} data-id="element-4531" data-mp="nblcehmnszhl" />}
                {activeTab === 'js' && <CodeEditor label="Custom JavaScript" language="javascript" placeholder="// Custom JS..." value={formData.js} onChange={e => setFormData({
              ...formData,
              js: e.target.value
            })} data-id="element-4532" data-mp="2y5g35k0ddy04" />}
              </div>
            </div>
            <GptCodeAssistant mode="form" currentCode={formData} onCodeGenerated={code => {
          setFormData(prev => ({
            html: code.html || prev.html,
            css: code.css !== undefined ? code.css : prev.css,
            js: code.js !== undefined ? code.js : prev.js
          }));
        }} data-id="element-4533" data-mp="1dn5kdsise9yy" />
          </div>
          <SettingsPanel templateName={templateName} setTemplateName={setTemplateName} description={description} setDescription={setDescription} category={category} setCategory={setCategory} team={team} region={region} data-id="element-4534" data-mp="2b8t4bdy22ztl" />
        </div>}

      {showPreview && <PreviewModal html={formData.html} css={formData.css} js={formData.js} onClose={() => setShowPreview(false)} title="Template Preview" data-id="element-4535" data-mp="2iq95vok7q892" />}

      <ConfirmModal isOpen={showBuilderConfirm} title="Switch to Builder" message="The builder starts with a blank canvas — your existing code cannot be imported into the visual builder. However, your code is preserved and will still be there if you switch back to Code mode without saving." confirmLabel="Switch to Builder" variant="warning" onCancel={() => setShowBuilderConfirm(false)} onConfirm={() => {
      setShowBuilderConfirm(false);
      setEditorMode('builder');
    }} data-id="element-4536" data-mp="28vzdi6v9t9yc" />

      <VersionHistoryPanel isOpen={showVersionHistory} onClose={() => setShowVersionHistory(false)} entityType="form" entityId={id || ''} entityName={templateName || 'Form Template'} onRestore={handleRestoreVersion} data-id="element-4537" data-mp="3ka4kz9up01yf" />
    </div>;
}
function SettingsPanel({
  templateName,
  setTemplateName,
  description,
  setDescription,
  category,
  setCategory,
  team,
  region
}: {
  templateName: string;
  setTemplateName: (v: string) => void;
  description: string;
  setDescription: (v: string) => void;
  category: string;
  setCategory: (v: string) => void;
  team: string;
  region: string;
}) {
  return <div className="space-y-6" data-id="element-4538" data-mp="fmredvin1z5o">
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 space-y-4" data-id="element-4539" data-mp="flq1rmlz54lu">
        <h3 className="text-sm font-medium text-gray-900 border-b border-gray-200 pb-2" data-id="element-4540" data-mp="km4bd9384mlz">
          Template Settings
        </h3>
        <div className="space-y-2" data-id="element-4541" data-mp="1r1enkanhrxl2">
          <label className="block text-sm font-medium text-gray-700" data-id="element-4542" data-mp="1w1134qqslpx1">
            Template Name
          </label>
          <input type="text" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={templateName} onChange={e => setTemplateName(e.target.value)} placeholder="My Form Template" data-id="element-4543" data-mp="3gisdpt8j23xd" />
        </div>
        <div className="space-y-2" data-id="element-4544" data-mp="31t49aj51up5f">
          <label className="block text-sm font-medium text-gray-700" data-id="element-4545" data-mp="31s2zsj3omapt">
            Description
          </label>
          <textarea rows={3} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={description} onChange={e => setDescription(e.target.value)} placeholder="Describe what this template is for..." data-id="element-4546" data-mp="2sth920o23pdy" />
        </div>
        <div className="space-y-2" data-id="element-4547" data-mp="m1q8kt1oc00r">
          <label className="block text-sm font-medium text-gray-700" data-id="element-4548" data-mp="m0x6i5dntyoy">
            Category
          </label>
          <select className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={category} onChange={e => setCategory(e.target.value)} data-id="element-4549" data-mp="h23rg8iq5zmo">
            <option value="" data-id="element-4550" data-mp="21n0iy2yxq97n">Select category...</option>
            {CATEGORIES.map(c => <option key={c} value={c} data-id="element-4551" data-mp="26m3qbu2e0ris">
                {c}
              </option>)}
          </select>
        </div>
        <div className="space-y-2" data-id="element-4552" data-mp="2ubfwsm6bylhe">
          <label className="block text-sm font-medium text-gray-700" data-id="element-4553" data-mp="2z9pvv7v6e68a">
            Team
          </label>
          <input type="text" className="block w-full rounded-md border-gray-300 bg-gray-50 shadow-sm sm:text-sm text-gray-500 cursor-not-allowed" value={team} disabled data-id="element-4554" data-mp="2la89t099l72m" />
          <p className="text-xs text-gray-400" data-id="element-4555" data-mp="19tqc6wtxkyee">
            Automatically set from your current workspace
          </p>
        </div>
        <div className="space-y-2" data-id="element-4556" data-mp="1dxpof386hb1y">
          <label className="block text-sm font-medium text-gray-700" data-id="element-4557" data-mp="16clw217ynubg">
            Region
          </label>
          <input type="text" className="block w-full rounded-md border-gray-300 bg-gray-50 shadow-sm sm:text-sm text-gray-500 cursor-not-allowed" value={region || '(All regions)'} disabled data-id="element-4558" data-mp="11eamzsphpnua" />
          <p className="text-xs text-gray-400" data-id="element-4559" data-mp="1bcqc5q4yin15">
            Automatically set from your current scope selection
          </p>
        </div>
      </div>
    </div>;
}